import { useState, useEffect } from "react";
import logo from "../assets/img/logotipo.png";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { scrollToAboutUs } from "./functions";

// Componente NavLink para simplificar los enlaces
const NavLink = ({
  to,
  href,
  onClick,
  className,
  children,
  isContact,
  isActive,
}) => {
  const activeStyles = isActive
    ? "text-base md:text-lg font-bold text-[#1e72b9] relative py-2 lg:py-3 lg:inline-flex lg:px-0 flex mx-8 lg:mr-0 lg:ml-6 transition-all duration-300 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#1e72b9]"
    : isContact
      ? "text-base md:text-lg font-semibold text-[#1e72b9] hover:text-[#29265b] relative py-2 lg:py-3 lg:inline-flex lg:px-0 flex mx-8 lg:mr-0 lg:ml-6 transition-all duration-300 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-[#1e72b9] after:transition-all after:duration-300 hover:after:w-full"
      : "text-base md:text-lg text-gray-700 hover:text-[#1e72b9] relative py-2 lg:py-3 lg:inline-flex lg:px-0 flex mx-8 lg:mr-0 lg:ml-6 transition-all duration-300 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-[#1e72b9] after:transition-all after:duration-300 hover:after:w-full";

  const baseStyles = activeStyles;

  if (to) {
    return (
      <Link to={to} className={`${baseStyles} ${className || ""}`}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      onClick={onClick}
      className={`${baseStyles} ${className || ""}`}
    >
      {children}
    </a>
  );
};

// Componente ActionButton para simplificar los botones
const ActionButton = ({ to, onClick, variant, children }) => {
  const baseClasses =
    "cursor-pointer text-white rounded-lg px-5 py-2.5 transition-all duration-300 transform";
  const variants = {
    primary: `${baseClasses} bg-[#0092ff] hover:bg-[#1e72b9] font-semibold shadow-md hover:shadow-lg hover:scale-[1.02]`,
    secondary: `${baseClasses} bg-[#2a3180] hover:bg-[#29265b] font-medium shadow-md hover:shadow-lg hover:scale-[1.02]`,
    profile:
      "cursor-pointer px-5 py-2.5 text-white rounded-lg bg-gradient-to-r from-[#2a3180] to-[#1e72b9] hover:from-[#29265b] hover:to-[#29265b] font-medium shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.02]",
  };

  if (to) {
    return (
      <Link to={to} className={variants[variant]}>
        {children}
      </Link>
    );
  }

  return (
    <a onClick={onClick} className={variants[variant]}>
      {children}
    </a>
  );
};

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const idRol_fk = localStorage.getItem("idRol_fk");
  const isAuthorized = ["1", "2"].includes(idRol_fk);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Función para manejar la navegación a secciones
  const handleSectionNavigation = (sectionId, e) => {
    e.preventDefault();

    // Si estamos en la página principal (landing), simplemente hacemos scroll
    if (location.pathname === "/") {
      scrollToAboutUs(sectionId);
    } else {
      // Si estamos en otra página, navegamos a la landing con un parámetro de sección
      navigate("/?section=" + sectionId);
    }
  };

  // Efecto para manejar el scroll a la sección cuando se carga la página
  useEffect(() => {
    // Si estamos en la landing y hay un parámetro de sección en la URL
    if (location.pathname === "/" && location.search) {
      const params = new URLSearchParams(location.search);
      const section = params.get("section");
      if (section) {
        // Pequeño timeout para asegurar que los elementos ya estén cargados
        setTimeout(() => {
          scrollToAboutUs(section);
        }, 100);
      }
    }
  }, [location]);

  // Configurar enlaces de navegación (siempre visibles)
  const navLinks = [
    {
      href: "#Methodology",
      onClick: (e) => handleSectionNavigation("Methodology", e),
      text: "Metodología",
      className: "",
    },
    {
      href: "#accordion-flush",
      onClick: (e) => handleSectionNavigation("accordion-flush", e),
      text: "Preguntas frecuentes",
      className: "",
    },
    {
      href: "#clients",
      onClick: (e) => handleSectionNavigation("clients", e),
      text: "Alianzas",
      className: "",
    },
  ];

  // Enlace de Contáctanos separado para posicionarlo a la derecha
  const contactLink = {
    to: "/contact",
    text: "Contáctanos",
    className: "",
  };

  // Enlace a socios: si está logueado va al catálogo privado, si no al público
  const sociosLink = {
    to: isAuthorized ? "/nuestros-socios" : "/socios",
    text: "Nuestros socios",
    className: "",
  };

  return (
    <header className="header fixed top-0 shadow-md bg-white z-40 w-full flex items-center transition">
      <div className="w-full">
        <div className="flex items-center justify-between relative">
          {/* Logo */}
          <div className="px-4">
            <a href="/" className="header-logo w-full block py-2">
              <img src={logo} alt="logo" className="w-[6rem] py2" />
            </a>
          </div>

          <div className="flex px-4 justify-between items-center w-full">
            {/* Botón menú móvil */}
            <div>
              <button
                onClick={toggleMobileMenu}
                id="navbarToggler"
                className="focus:ring-transparent absolute right-4 top-1/2 translate-y-[-50%] lg:hidden focus:ring-2 ring-primary px-3 py-[6px] rounded-lg flex items-center"
              >
                <i className="bx bx-menu-alt-right text-3xl"></i>
              </button>

              {/* Menú de navegación */}
              <nav
                id="navbarCollapse"
                className={`absolute py-3 lg:py-0 lg:px-4 xl:px-6 bg-white lg:bg-transparent shadow-md rounded-sm max-w-[250px] w-full lg:max-w-full lg:w-full right-2 top-full ${
                  isMobileMenuOpen ? "" : "hidden"
                } lg:block lg:static lg:shadow-none`}
              >
                <ul className="block lg:flex">
                  {/* Enlaces principales de navegación */}
                  {navLinks.map((link, index) => (
                    <li key={index} className="relative group">
                      <NavLink
                        to={link.to}
                        href={link.href}
                        onClick={link.onClick}
                        className={link.className}
                      >
                        {link.text}
                      </NavLink>
                    </li>
                  ))}

                  {/* Enlace socios - siempre visible */}
                  <li className="relative group">
                    <NavLink
                      to={sociosLink.to}
                      className={sociosLink.className}
                    >
                      {sociosLink.text}
                    </NavLink>
                  </li>

                  {/* Contáctanos - Solo móvil (en escritorio está a la derecha) */}
                  {!isAuthorized && (
                    <li className="relative group lg:hidden">
                      <NavLink
                        to={contactLink.to}
                        isContact={true}
                        isActive={location.pathname === "/contact"}
                      >
                        {contactLink.text}
                      </NavLink>
                    </li>
                  )}

                  {/* Botones de acción - Solo para móvil, ocultos si ya inició sesión */}
                  {!isAuthorized && (
                    <>
                      <li className="relative group md:hidden flex">
                        <ActionButton to="/registrate" variant="primary">
                          Regístrate
                        </ActionButton>
                      </li>

                      <li className="relative group md:hidden flex">
                        <NavLink onClick={() => navigate("/registraempresa")}>
                          Acceso industria
                        </NavLink>
                      </li>

                      <li className="relative group md:hidden flex">
                        <NavLink onClick={() => navigate("/login")}>
                          Acceso a miembros
                        </NavLink>
                      </li>
                    </>
                  )}
                </ul>
              </nav>
            </div>

            {/* Botones de acción - Vista de escritorio */}
            <div className="md:flex items-center cursor-pointer hidden flex-wrap justify-end pr-16 lg:pr-0 py-1.5 gap-2">
              {/* Contáctanos a la derecha */}
              {!isAuthorized && (
                <NavLink
                  to={contactLink.to}
                  isContact={true}
                  isActive={location.pathname === "/contact"}
                >
                  {contactLink.text}
                </NavLink>
              )}

              {!isAuthorized && (
                <div className="ml-3">
                  <ActionButton to="/registrate" variant="primary">
                    Regístrate
                  </ActionButton>
                </div>
              )}

              <div className="ml-3">
                {["1", "2"].includes(idRol_fk) ? (
                  <ActionButton
                    onClick={() => navigate("/empresas")}
                    variant="profile"
                  >
                    Ver perfil
                  </ActionButton>
                ) : (
                  <ActionButton
                    onClick={() => navigate("/login")}
                    variant="secondary"
                  >
                    Socios
                  </ActionButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
